import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import axios from 'axios';
import { captureAnalytics } from '../actions/preload';

const useReferralMessages = (slug, cable, user, loggedIn) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referrerOrg, setReferrerOrg] = useState(null);
  const [partnerOrg, setPartnerOrg] = useState(null);
  const [showStartMessage, setShowStartMessage] = useState(true);
  const [hasUnread, setHasUnread] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [shareRevoked, setShareRevoked] = useState(false);
  const messagesEndRef = useRef(null);
  const subscriptionRef = useRef(null);
  const connectionRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const isActiveRef = useRef(true);
  const mountedRef = useRef(true);
  const sendErrorTimeoutRef = useRef(null);
  const refreshMessagesRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      clearTimeout(sendErrorTimeoutRef.current);
      sendErrorTimeoutRef.current = null;
    };
  }, []);

  // Fetch message history
  useEffect(() => {
    let isMounted = true;
    let requestId = 0;
    let latestSuccessfulRequestId = 0;
    let loadedMessageIds = new Set();
    let hasLoadedHistory = false;
    setHistoryError(null);
    setMessages([]);
    setReferrerOrg(null);
    setPartnerOrg(null);
    setShowStartMessage(true);
    setShareRevoked(false);
    setSendError(null);

    if (!slug) {
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const fetchMessages = async () => {
      requestId += 1;
      const currentRequestId = requestId;
      const knownMessageIds = loadedMessageIds;
      const shouldReconcileHistory = hasLoadedHistory;
      try {
        const url = `/api/v1/referral_entities/${slug}/messages`;
        const response = await axios.get(url);
        if (!isMounted || currentRequestId < latestSuccessfulRequestId) return;
        const { data } = response;
        let history = data.messages || [];
        let page = data;
        // Reconnect can miss more than one page. Walk back to the history we already
        // loaded; an empty prior history requires walking to the beginning.
        // Initial entry still loads only the newest page.
        while (shouldReconcileHistory && page.has_more && page.next_cursor
          && !history.some((message) => knownMessageIds.has(message.message_id))) {
          // Each page provides the cursor required for the next request.
          // eslint-disable-next-line no-await-in-loop
          const older = await axios.get(url, { params: { before_timestamp: page.next_cursor } });
          if (!isMounted || currentRequestId < latestSuccessfulRequestId) return;
          page = older.data;
          history = [...(page.messages || []), ...history];
        }
        latestSuccessfulRequestId = currentRequestId;
        loadedMessageIds = new Set(history.map((message) => message.message_id));
        hasLoadedHistory = true;
        // Preserve socket messages received while the history request was in flight.
        setMessages((current) => {
          const historyIds = new Set(history.map((message) => message.message_id));
          return [...history, ...current.filter((message) => !historyIds.has(message.message_id))]
            .sort((left, right) => new Date(left.timestamp) - new Date(right.timestamp));
        });
        setReferrerOrg(data.referrer_org);
        setPartnerOrg(data.partner_org);
        if (history.length) setShowStartMessage(false);
        if (currentRequestId === requestId) setHistoryError(null);
      } catch {
        if (isMounted && currentRequestId === requestId && currentRequestId > latestSuccessfulRequestId) {
          setHistoryError('Message history could not be loaded. Please try again.');
        }
      } finally {
        if (isMounted && (hasLoadedHistory || currentRequestId === requestId)) setLoading(false);
      }
    };

    setLoading(true);
    refreshMessagesRef.current = fetchMessages;
    fetchMessages();

    return () => {
      isMounted = false;
      refreshMessagesRef.current = null;
    };
  }, [slug]);

  // Subscribe to ActionCable channel
  useEffect(() => {
    setConnected(false);
    if (!slug || !cable || !loggedIn || !user) return undefined;

    let active = true;
    const connection = {
      slug, cable, user, confirmed: false, rejected: false,
    };
    connectionRef.current = connection;
    const disable = () => {
      if (!active) return;
      connection.confirmed = false;
      setConnected(false);
    };

    const subscription = cable.subscriptions.create(
      {
        channel: 'ReferralMessagesChannel',
        slug,
      },
      {
        connected: () => {
          if (!active || connection.rejected) return;
          connection.confirmed = true;
          setConnected(true);
          // Cover the gap between loading history and subscribing, as well as reconnects.
          refreshMessagesRef.current?.();
        },
        disconnected: disable,
        rejected: () => {
          if (!active) return;
          connection.rejected = true;
          disable();
        },
        received: (response) => {
          if (!active || connection.rejected) return;
          if (response.type === 'send_error') {
            console.error('Message send failed:', response.error);
            setSendError(response.error);
            clearTimeout(sendErrorTimeoutRef.current);
            const timeoutId = setTimeout(() => {
              if (sendErrorTimeoutRef.current !== timeoutId || !mountedRef.current) return;
              sendErrorTimeoutRef.current = null;
              setSendError(null);
            }, 5000);
            sendErrorTimeoutRef.current = timeoutId;
            return;
          }
          if (response.type === 'share_revoked') {
            connection.rejected = true;
            disable();
            setShareRevoked(true);
            return;
          }
          setMessages((prev) => {
            if (prev.some((m) => m.message_id === response.message_id)) return prev;
            return [...prev, response];
          });
          setShowStartMessage(false);
          if (!isActiveRef.current) {
            setHasUnread(true);
          }
        },
      },
    );

    subscriptionRef.current = subscription;

    return () => {
      active = false;
      connection.confirmed = false;
      subscription.unsubscribe();
      if (subscriptionRef.current === subscription) subscriptionRef.current = null;
      if (connectionRef.current === connection) connectionRef.current = null;
    };
  }, [slug, cable, loggedIn, user]);

  const hasLoadedRef = useRef(false);

  // Auto-scroll when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      if (container) {
        if (hasLoadedRef.current) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        } else {
          container.scrollTop = container.scrollHeight;
        }
      }
      if (!loading && messages.length > 0) {
        hasLoadedRef.current = true;
      }
    }
  }, [messages, loading]);

  const sendMessage = useCallback((content) => {
    if (!content.trim()) return false;

    const connection = connectionRef.current;
    const canSendNow = loggedIn && partnerOrg && !shareRevoked && connection?.confirmed
      && connection.slug === slug && connection.cable === cable && connection.user === user;
    const sent = canSendNow && subscriptionRef.current?.send({
      content: content.trim(),
      sender_id: user.uid,
      sender_org: user.organization,
    });
    captureAnalytics('referral_chat_send_attempted', { sent: Boolean(sent) });
    if (!sent) {
      setSendError('Message not sent. Check your connection and try again.');
      return false;
    }
    clearTimeout(sendErrorTimeoutRef.current);
    setSendError(null);
    return true;
  }, [slug, cable, user, loggedIn, partnerOrg, shareRevoked]);

  const retryHistory = useCallback(() => refreshMessagesRef.current?.(), []);

  const markAsRead = useCallback((messageId) => {
    if (subscriptionRef.current) {
      subscriptionRef.current.perform('mark_as_read', { message_id: messageId });
    }
  }, []);

  const setActive = useCallback((active) => {
    isActiveRef.current = active;
    if (active) {
      setHasUnread(false);
    }
  }, []);

  const connection = connectionRef.current;
  const canSend = Boolean(connected && loggedIn && partnerOrg && !shareRevoked && connection?.confirmed
    && connection.slug === slug && connection.cable === cable && connection.user === user);

  return {
    messages,
    sendMessage,
    canSend,
    markAsRead,
    loading,
    referrerOrg,
    partnerOrg,
    showStartMessage,
    hasUnread,
    setActive,
    messagesEndRef,
    sendError,
    historyError,
    retryHistory,
    shareRevoked,
  };
};

export default useReferralMessages;
